import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, useNavigate } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import Select from "react-select";
import TablePagination from "./TablePagination";
import { selectStyles } from "../helper/SelectStyles";
import { Tooltip } from "bootstrap/dist/js/bootstrap.bundle.min.js";
import { getClients, deleteClient, toggleClientStatus } from "../Apis/ClientAPIs";
import { getManagers } from "../Apis/ManagerAPIs";
import { getClientGroups } from "../Apis/ClientGroupAPIs";
import usePermissions from "../hook/userPermission";
import { formatDateTime } from "../helper/DateHelper";

const ExhibitorsLayer = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [members, setMembers] = useState([]);
  const [managers, setManagers] = useState([]);
  const [managerMap, setManagerMap] = useState({});
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterMobile, setFilterMobile] = useState("");
  const [filterManager, setFilterManager] = useState("all");
  const [filterType, setFilterType] = useState("manager"); // "manager" or "expo"
  const [filterGroup, setFilterGroup] = useState("all");
  const [expoGroups, setExpoGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isUpdating, setIsUpdating] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);

  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedMemberHistory, setSelectedMemberHistory] = useState([]);
  const [selectedMemberName, setSelectedMemberName] = useState("");

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [debouncedMobile, setDebouncedMobile] = useState("");

  // Debounce inputs to prevent excessive API calls
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedMobile(filterMobile);
    }, 500);
    return () => clearTimeout(handler);
  }, [filterMobile]);

  const clearFilters = () => {
    setSearchTerm("");
    setFilterMobile("");
    setFilterManager("all");
    setFilterGroup("all");
    setFilterType("manager");
    setCurrentPage(0);
  };

  const fetchInitialData = async () => {
    setIsLoading(true);
    try {
      // Fetch managers to build lookup map and dropdown options
      const manRes = await getManagers({ page: 0, limit: 1000, isActive: true });
      let mgrMapping = {};
      if (manRes.status) {
        const mData = manRes.response?.data || manRes.response || [];
        setManagers(Array.isArray(mData) ? mData : []);
        mData.forEach((m) => {
          mgrMapping[m._id || m.id] = m.name;
        });
        setManagerMap(mgrMapping);
      }

      // Fetch Expo Groups
      const groupRes = await getClientGroups({ page: 0, limit: 1000, groupType: 'Expo' });
      if (groupRes.status) {
        const gData = groupRes.response?.data || groupRes.response || [];
        setExpoGroups(Array.isArray(gData) ? gData : []);
      }

      // Fetch Clients
      const clientRes = await getClients({
        page: currentPage,
        limit: rowsPerPage, 
        search: debouncedSearchTerm || undefined,
        mobileNumber: debouncedMobile || undefined,
        managerId: filterType === "manager" && filterManager !== "all" ? filterManager : undefined,
        groupId: filterType === "expo" && filterGroup !== "all" ? filterGroup : undefined,
        isActive: true, // As specified in the API query: isActive=true
      });

      if (clientRes.status) {
        const data = clientRes.response?.data || clientRes.response || [];
        setMembers(data);
        setTotalRecords(clientRes.response?.total || data.length);
      }
    } catch (err) {
      console.error("Error fetching data: ", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, [
    currentPage,
    rowsPerPage,
    debouncedSearchTerm,
    debouncedMobile,
    filterManager,
    filterGroup,
    filterType
  ]);

  useEffect(() => {
    const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
    const tooltipList = [...tooltipTriggerList].map((el) => new Tooltip(el, { customClass: "theme-tooltip" }));
    return () => tooltipList.forEach((t) => t.dispose());
  }, [members]);

  const managerOptions = [
    { value: "all", label: "All Managers" },
    ...managers.map((m) => ({ value: m._id || m.id, label: m.name })),
  ];

  const expoGroupOptions = [
    { value: "all", label: "All Expo Groups" },
    ...expoGroups.map((g) => ({ value: g._id || g.id, label: g.groupName })),
  ];

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value));
    setCurrentPage(0);
  };

  const handleHistoryClick = (member) => {
    setSelectedMemberHistory(member.statusDetails || []);
    setSelectedMemberName(member.companyName || member.keyPerson || "");
    setShowHistoryModal(true);
  };

  const handleDeleteClick = (member) => {
    setMemberToDelete(member);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (memberToDelete) {
      setIsUpdating(true);
      const res = await deleteClient(memberToDelete._id || memberToDelete.id);
      if (res.status) {
        toast.success("Exhibitor Deleted Successfully");
        fetchInitialData();
        setShowDeleteModal(false);
        setMemberToDelete(null);
      }
      setIsUpdating(false);
    }
  };

  const handleCloseDelete = () => {
    setShowDeleteModal(false);
    setMemberToDelete(null);
  };

  const handleStatusToggle = async (id, currentStatus) => {
    setIsUpdating(true);
    const res = await toggleClientStatus(id, { isActive: !currentStatus });
    if (res.status) {
      toast.success("Exhibitor Status Updated Successfully");
      fetchInitialData();
    }
    setIsUpdating(false);
  };

  return (
    <div className="card h-100 p-0 radius-12 overflow-hidden border-0 shadow-sm">
      <div
        className="card-header border-bottom py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between"
        style={{
          background:
            "linear-gradient(135deg, #000000ff, #bb0007ff, #ff0000ff)",
        }}
      >
        <div className="d-flex align-items-center flex-wrap gap-3">
          <h6 className="text-white pb-2 mb-0">Exhibitors</h6>
        </div>
      </div>
      <div className="card-body p-24">
        {/* Search & Filters */}
        <div className="mb-24 p-16 radius-8 bg-neutral-50 border">
          <div className="row gy-3">
            {/* Global Search Name/Company */}
            <div className="col-md-3">
              <label className="form-label text-xs fw-bold text-secondary-light uppercase p-0 m-0 mb-1">
                Search Name/Company
              </label>
              <div className="position-relative">
                <input
                  type="text"
                  className="form-control radius-8 ps-40"
                  style={{ height: "44px" }}
                  placeholder="Search name, company..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(0);
                  }}
                />
                <Icon
                  icon="ion:search-outline"
                  className="position-absolute top-50 translate-middle-y text-secondary-light"
                  style={{ fontSize: "20px", left: "14px" }}
                />
              </div>
            </div>

            {/* Filter By Type Dropdown */}
            <div className="col-md-2">
              <label className="form-label text-xs fw-bold text-secondary-light uppercase p-0 m-0 mb-1">
                Filter By
              </label>
              <Select
                options={[
                  { value: "manager", label: "Manager" },
                  { value: "expo", label: "Expo Group" }
                ]}
                value={{ value: filterType, label: filterType === "manager" ? "Manager" : "Expo Group" }}
                onChange={(val) => {
                  setFilterType(val.value);
                  setFilterManager("all");
                  setFilterGroup("all");
                  setCurrentPage(0);
                }}
                styles={selectStyles()}
                isSearchable={false}
              />
            </div>

            {/* Conditional Dropdown */}
            <div className="col-md-3">
              <label className="form-label text-xs fw-bold text-secondary-light uppercase p-0 m-0 mb-1">
                {filterType === "manager" ? "Event Manager" : "Expo Group"}
              </label>
              {filterType === "manager" ? (
                <Select
                  options={managerOptions}
                  value={managerOptions.find((opt) => opt.value === filterManager)}
                  onChange={(val) => {
                    setFilterManager(val ? val.value : "all");
                    setCurrentPage(0);
                  }}
                  styles={selectStyles()}
                  placeholder="Select Manager"
                />
              ) : (
                <Select
                  options={expoGroupOptions}
                  value={expoGroupOptions.find((opt) => opt.value === filterGroup)}
                  onChange={(val) => {
                    setFilterGroup(val ? val.value : "all");
                    setCurrentPage(0);
                  }}
                  styles={selectStyles()}
                  placeholder="Select Expo Group"
                />
              )}
            </div>

            {/* Mobile Search input */}
            <div className="col-md-2">
              <label className="form-label text-xs fw-bold text-secondary-light uppercase p-0 m-0 mb-1">
                Mobile Number
              </label>
              <input
                type="text"
                className="form-control radius-8"
                style={{ height: "44px" }}
                placeholder="Search Mobile"
                value={filterMobile}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "");
                  setFilterMobile(val);
                  setCurrentPage(0);
                }}
              />
            </div>

            {/* Clear button */}
            <div className="col-md-2 d-flex align-items-end">
              <button
                className="btn btn-outline-danger h-44-px w-100 radius-8 fw-bold d-flex align-items-center justify-content-center gap-2"
                onClick={clearFilters}
                title="Clear Filters"
              >
                <Icon icon="solar:restart-bold" className="text-xl" />
                Reset
              </button>
            </div>
          </div>
        </div>

        {/* Table Structure */}
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr style={{ backgroundColor: "#f7e7da" }}>
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                  S.No
                </th>
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                  Company Name
                </th>
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                  Key Person
                </th>
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                  Mobile Number
                </th>
                {/* <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                  WhatsApp
                </th> */}
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                  Email
                </th>
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                  Group Type
                </th>
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                  Manager
                </th>
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                  Ratings
                </th>
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                  Status
                </th>
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="10" className="text-center py-4">
                    Loading...
                  </td>
                </tr>
              ) : members.length > 0 ? (
                members.map((member, index) => {
                  const mId = typeof member.managerId === 'object' ? (member.managerId?._id || member.managerId?.id) : member.managerId;
                  const mName = managerMap[mId] || (typeof member.managerId === 'object' ? member.managerId?.name : '') || member.managerId || "-";

                  return (
                    <tr key={member._id || member.id}>
                      <td>{currentPage * rowsPerPage + index + 1}</td>
                      <td>{member.companyName || "-"}</td>
                      <td>{member.keyPerson || "-"}</td>
                      <td>{member.mobile1 || "-"}</td>
                      {/* <td className="text-success-600 fw-bold">
                        {member.whatsappNumber || "-"}
                      </td> */}
                      <td>{member.email || "-"}</td>
                      <td>{member.groupType || "-"}</td>
                      <td>{mName}</td>
                      <td>
                        <div className="d-flex align-items-center gap-1">
                          <Icon icon="solar:star-bold" className="text-warning" />
                          <span className="fw-bold">
                            {(() => {
                              const r = String(member.ratings || "");
                              const mapping = {
                                "1": "VIP",
                                "2": "ENQUIRY",
                                "3": "OTHER EXHIBITOR",
                                "4": "OWN EXHIBITOR",
                              };
                              return mapping[r] || r || "OTHER EXHIBITOR";
                            })()}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span
                          className={`badge px-12 py-4 radius-pill fw-bold text-sm ${member.isActive
                              ? "bg-success-focus text-success-600"
                              : "bg-danger-focus text-danger-600"
                            } ${isUpdating ? "opacity-50" : ""}`}
                          style={{ minWidth: "80px" }}
                        >
                          {member.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-10">
                          <Link
                            to={`/clients/view/${member._id || member.id}`}
                            state={{ from: "/exhibitors" }}
                            className="btn-view-custom fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                            style={{
                              backgroundColor: "#eef2ff",
                              color: "#4338ca",
                              border: "1px solid #e0e7ff",
                            }}
                            title="View"
                          >
                            <Icon icon="lucide:eye" className="menu-icon" />
                          </Link>
                          <button
                            type="button"
                            onClick={() => handleHistoryClick(member)}
                            className="fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0"
                            style={{
                              backgroundColor: "#f3e8ff",
                              color: "#7c3aed",
                              border: "1px solid #ddd6fe",
                            }}
                            title="Status History"
                          >
                            <Icon icon="lucide:history" className="text-xl" />
                          </button>
                          {hasPermission("clients", "edit") && (
                            <Link
                              to={`/clients/edit/${member._id || member.id}`}
                              state={{ from: "/exhibitors" }}
                              className="btn-edit-custom fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                              title="Edit"
                            >
                              <Icon icon="lucide:edit" className="menu-icon" />
                            </Link>
                          )}
                          {hasPermission("clients", "delete") && (
                            <button
                              type="button"
                              onClick={() => handleDeleteClick(member)}
                              className="bg-danger-focus text-danger-600 bg-hover-danger-200 w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0"
                              title="Delete"
                            >
                              <Icon icon="lucide:trash-2" className="text-xl" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="10" className="text-center py-4">
                    No members found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <TablePagination
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / rowsPerPage) || 1}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          totalRecords={totalRecords}
        />
      </div>

      <Modal show={showDeleteModal} onHide={handleCloseDelete} centered>
        <Modal.Body className="text-center p-5">
          <div className="d-flex justify-content-center mb-3">
            <div className="bg-danger-focus rounded-circle d-flex justify-content-center align-items-center w-64-px h-64-px">
              <Icon icon="lucide:trash-2" className="text-danger-600 text-xxl" />
            </div>
          </div>
          <h5 className="mb-3">Are you sure?</h5>
          <p className="text-secondary-light mb-4">
            Do you want to delete exhibitor "
            {memberToDelete?.companyName || memberToDelete?.keyPerson}"?
          </p>
          <div className="d-flex justify-content-center gap-3">
            <Button
              variant="outline-secondary"
              className="px-32"
              onClick={handleCloseDelete}
            >
              Cancel
            </Button>
            <Button
              className="px-32 border-0"
              onClick={confirmDelete}
              disabled={isUpdating}
              style={{ backgroundColor: "#ed1c24", color: "white" }}
            >
              {isUpdating ? "Deleting..." : "Confirm"}
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={showHistoryModal} onHide={() => setShowHistoryModal(false)} size="lg" centered>
        <Modal.Header closeButton className="border-bottom py-16 px-24 text-white radius-top-16" style={{ background: "linear-gradient(135deg, #000000 0%, #bb0007 50%, #ed1c24 100%)" }}>
          <Modal.Title className="fw-bold text-white mb-0" style={{ fontSize: "16px" }}>
            Status History: {selectedMemberName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-24">
          <div className="table-responsive">
            <table className="table bordered-table sm-table mb-0">
              <thead>
                <tr style={{ background: "linear-gradient(135deg, #000000, #4f1818, #ed1c24)" }}>
                  <th scope="col" style={{ color: "white", fontWeight: "bold", fontSize: "12px", width: "60px" }}>S.No</th>
                  <th scope="col" style={{ color: "white", fontWeight: "bold", fontSize: "12px", width: "130px" }}>Changed By</th>
                  <th scope="col" style={{ color: "white", fontWeight: "bold", fontSize: "12px", width: "150px" }}>Date & Time</th>
                  <th scope="col" style={{ color: "white", fontWeight: "bold", fontSize: "12px", minWidth: "250px" }}>Remarks</th>
                  <th scope="col" style={{ color: "white", fontWeight: "bold", fontSize: "12px", width: "120px" }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {selectedMemberHistory && selectedMemberHistory.length > 0 ? (
                  selectedMemberHistory.map((item, index) => (
                    <tr key={index}>
                      <td className="text-xs">{index + 1}</td>
                      <td className="text-xs fw-semibold text-secondary-light">
                        {item.createdByName || "-"}
                      </td>
                      <td className="text-xs text-secondary-light">
                        {formatDateTime(item.date)}
                      </td>
                      <td className="text-xs text-dark fw-medium" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                        {item.remarks || "-"}
                      </td>
                      <td className="text-xs">
                        <span
                          className={`badge px-12 py-4 radius-pill fw-bold ${item.status === 'Converted' ? 'bg-success-focus text-success-main' :
                              item.status === 'High Intent' ? '' :
                                item.status === 'In Discussions' ? 'bg-warning-focus text-warning-main' :
                                  item.status === 'Not Interested' ? 'bg-danger-focus text-danger-main' :
                                    item.status === 'Potential' ? 'bg-info-focus text-info-main' :
                                      'bg-neutral-100 text-neutral-600'
                            }`}
                          style={item.status === 'High Intent' ? {
                            backgroundColor: "#f3e8ff",
                            color: "#6d28d9",
                            border: "1px solid #ddd6fe"
                          } : {}}
                        >
                          {item.status || "-"}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center py-3 text-secondary-light italic text-xs">
                      No status history found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-top p-16">
          <Button
            variant="secondary"
            className="px-32 radius-8"
            onClick={() => setShowHistoryModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ExhibitorsLayer;
